import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function SendBackDialog({ open, onClose, practiceId, onDone }) {
  const [reason, setReason] = useState("");
  const [firstConfirm, setFirstConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = reason.trim().length >= 10 && firstConfirm;
  const reset = () => { setReason(""); setFirstConfirm(false); };
  const doSendBack = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/practices/${practiceId}/send-back`, { reason: reason.trim() });
      toast.success("Pratica rimandata indietro. Operatore precedente notificato.");
      onDone && onDone();
      reset(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ArrowsClockwise size={18} weight="fill" /> Rimanda indietro
          </DialogTitle>
          <DialogDescription>
            La pratica tornerà all'operatore dello step precedente. Descrivi il problema
            per aiutarlo a correggere: il messaggio comparirà con un banner nella chat pratica.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Motivo (min. 10 caratteri) *
            </label>
            <Textarea
              rows={4}
              placeholder="Es. Il documento allegato non è leggibile, ricarica una versione a piena risoluzione."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              data-testid="send-back-reason"
            />
            <div className={`text-[11px] mt-0.5 ${reason.trim().length >= 10 ? "text-emerald-600" : "text-slate-400"}`}>
              {reason.trim().length}/10 caratteri minimi
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-red-200 bg-red-50/50">
            <input
              type="checkbox"
              checked={firstConfirm}
              onChange={(e) => setFirstConfirm(e.target.checked)}
              data-testid="send-back-first-confirm"
              className="mt-0.5"
            />
            <span className="text-slate-700">
              Confermo di voler <strong>rimandare indietro</strong> questa pratica.
              L'operatore precedente sarà notificato immediatamente e dovrà correggere/integrare
              quanto indicato.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={(e) => { e.stopPropagation(); reset(); onClose(); }}>Annulla</Button>
          <Button
            onClick={doSendBack}
            disabled={!canSubmit || submitting}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="confirm-send-back"
          >
            {submitting ? "Invio…" : "Conferma respinta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter104: dialog "Rilavora" (Admin/Supervisor) — riapre pratica completata

// ============================================================
// Professional: revoca incarico (solo in "assegnazione")
// ============================================================
export function ProRevokeDialog({ open, onClose, practiceId, serviceName, paidByClient, onDone }) {
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/practices/${practiceId}/revoke`, { message: "" });
      toast.success(paidByClient
        ? "Incarico revocato · rimborso inviato al cliente"
        : "Incarico revocato · nessun addebito");
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la revoca");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={18} weight="fill" color="#B45309" />
            Revoca incarico
          </DialogTitle>
          <DialogDescription>
            {paidByClient ? (
              <>
                Sicuro di voler annullare "<strong>{serviceName}</strong>"? Il tuo cliente riceverà il rimborso e l'eventuale credito accumulato verrà annullato.
              </>
            ) : (
              <>
                Sicuro di voler annullare "<strong>{serviceName}</strong>"? Nulla ti sarà addebitato e il plafond verrà ripristinato.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="pro-revoke-cancel">Torna indietro</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={confirm}
            disabled={submitting}
            data-testid="pro-revoke-confirm"
          >
            {submitting ? "Revoca in corso…" : "Sì, revoca"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
